import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useFlexLayout, useRowSelect, useSortBy, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';

import { useEnableLotTracking } from '../../../../../../../feature-flags/hooks/use-enable-lot-tracking';
import { useIsUnitsOfMeasureAvailable } from '../../../../../../../feature-gates/use-is-units-of-measure-available';
import { openProductDetailModal } from '../../../../../../../store/products/products.actions';
import { getReadonlyColumns } from './columns';
import { ProductGridFooter } from '../product-grid-footer/product-grid-footer';
import styles from './readonly-product-grid.styles.module.less';

import type { TableOptions } from 'react-table';
import type { Product, ReadonlyFlattenedProductRow } from '../../types';

type ReadonlyProductGridProps = {
  products: Product[];
};

type ReadonlyProductGridTableOptions = TableOptions<ReadonlyFlattenedProductRow> & {
  getRowId: (row: ReadonlyFlattenedProductRow) => string;
};

const flattenProductsWithQuantity = (products: Product[]): ReadonlyFlattenedProductRow[] => {
  const rows: ReadonlyFlattenedProductRow[] = [];

  products.forEach((product) => {
    const locationsWithQuantity = product.locations.filter((loc) => loc.quantity > 0);
    const rowCount = locationsWithQuantity.length;

    locationsWithQuantity.forEach((loc, index) => {
      rows.push({
        productId: product.productId,
        rowId: loc.rowId,
        locationId: loc.locationId,
        name: product.name,
        sku: product.sku,
        thumbnailUrl: product.thumbnailUrl,
        location: loc.location,
        quantity: loc.quantity,
        lotId: loc.lotId,
        lotUuid: loc.lotUuid,
        expirationDate: loc.expirationDate,
        isFirstProductRow: index === 0,
        isLastProductRow: index === rowCount - 1,
        rowCount,
        isLocationUnavailable: loc.isLocationUnavailable,
        unitsOfMeasure: product.unitsOfMeasure,
        unitOfMeasureId: loc.unitOfMeasureId,
      });
    });
  });

  return rows;
};

export const ReadonlyProductGrid = ({ products }: ReadonlyProductGridProps) => {
  const dispatch = useDispatch();
  const isLotTrackingEnabled = useEnableLotTracking();
  const { isAvailable: isUomEnabled } = useIsUnitsOfMeasureAvailable();

  const flattenedRows = useMemo(() => flattenProductsWithQuantity(products), [products]);

  const totalProducts = useMemo(() => new Set(flattenedRows.map((row) => row.productId)).size, [flattenedRows]);

  const openProductDetails = useCallback(
    (productId: string) => {
      if (productId) {
        dispatch(openProductDetailModal(productId));
      }
    },
    [dispatch],
  );

  const columns = useMemo(() => {
    return getReadonlyColumns({
      showAdvancedColumns: isLotTrackingEnabled,
      isUomEnabled,
      openProductDetails,
    });
  }, [isLotTrackingEnabled, isUomEnabled, openProductDetails]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: flattenedRows,
      getRowId: (row: ReadonlyFlattenedProductRow) => `${row.productId}-${row.rowId}`,
    } as ReadonlyProductGridTableOptions,
    useSortBy,
    useFlexLayout,
    useRowSelect,
  );

  const getBodyRowClassName = useCallback(
    (row: { original: ReadonlyFlattenedProductRow }) => (row.original.isLastProductRow ? '' : styles['no-bottom-border']),
    [],
  );

  return (
    <div className={styles['product-table-container']}>
      <ReactTableWrapper
        className={styles['readonly-product-table']}
        getBodyRowClassName={getBodyRowClassName}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        headerRowClassName={styles['header-row']}
        prepareRow={prepareRow}
        rows={rows}
      />
      <ProductGridFooter totalProducts={totalProducts} />
    </div>
  );
};
